import moment from "moment";
import React from "react";

/**
 * Renders a key-value pair for primitive types (string, number, boolean).
 */
const renderPrimitiveView = ({ key, value }) => {
  return (
    <div className="d-flex gap-3">
      <span style={{ flex: 1, textTransform: "capitalize" }}>{key}:</span>
      <b>
        {typeof value === "boolean"
          ? value.toString() // Convert booleans to string
          : value ?? "N/A"}{" "}
        {/* Handle null/undefined as "N/A" */}
      </b>
    </div>
  );
};

/**
 * Renders a date field with specific formatting.
 */
const renderDateField = ({ key, value }) => {
  return (
    <div className="d-flex gap-3">
      <span style={{ flex: 1, textTransform: "capitalize" }}>{key}:</span>
      <b>{moment(value).isValid() ? moment(value).format("lll") : value}</b>
    </div>
  );
};

/**
 * Renders a badge for specific keys or values.
 */
const renderBadgeView = ({ key, value }) => {
  const badges = ["paymentResult", "transactionResult", "responseMessage"];
  const badgeClass =
    badges.includes(key) && value?.toLowerCase() === "success"
      ? "badge badge-success"
      : badges.includes(key) && value?.toLowerCase() === "failed"
      ? "badge badge-danger"
      : "";

  return (
    <div className="d-flex gap-3">
      <span style={{ flex: 1, textTransform: "capitalize" }}>{key}:</span>
      <div className={badgeClass}>
        <b>{value}</b>
      </div>
    </div>
  );
};

/**
 * Recursively renders objects and arrays with infinite nesting.
 */
const renderObjectView = ({ key, value }) => {
  if (Array.isArray(value)) {
    // Handle arrays
    return (
      <div className="d-flex flex-column gap-3">
        <span style={{ textTransform: "capitalize" }}>{key}:</span>
        <div style={{ marginLeft: "1rem", borderLeft: "2px solid #ccc" }}>
          {value.map((item, index) => {
            return (
              <div key={index} style={{ padding: "0.5rem" }}>
                {renderValueView({ key: `${key} ${index + 1}`, value: item })}
              </div>
            );
          })}
        </div>
      </div>
    );
  } else if (typeof value === "object" && value !== null) {
    // Handle objects
    return (
      <div className="card">
        <div style={{ backgroundColor: "#f1f1f1", padding: "1rem" }}>
          <span style={{ flex: 1, textTransform: "capitalize" }}>
            <b>{key}</b>
          </span>
        </div>
        <div style={{ marginLeft: "1rem", borderLeft: "2px solid #ccc" }}>
          {Object.entries(value).map(([childKey, childValue]) => (
            <div key={childKey} style={{ padding: "0.5rem" }}>
              {renderValueView({ key: childKey, value: childValue })}
            </div>
          ))}
        </div>
      </div>
    );
  } else {
    return renderPrimitiveView({ key, value });
  }
};

/**
 * Determines the appropriate renderer for a given value.
 */
const renderValueView = ({ key, value }) => {
  if (
    typeof value === "string" &&
    moment(value, moment.ISO_8601, true).isValid()
  ) {
    // Render as date if the value is a valid ISO date string
    return renderDateField({ key, value });
  } else if (typeof value === "string" || typeof value === "number") {
    // Render as badge for specific keys or as a primitive
    return renderBadgeView({ key, value });
  } else if (typeof value === "object") {
    // Render objects or arrays recursively
    return renderObjectView({ key, value });
  } else {
    // Render primitive values
    return renderPrimitiveView({ key, value });
  }
};

/**
 * Main component to display a single transaction with nested data.
 */
class SingleTransactionView extends React.Component {
  render() {
    const { transaction } = this.props;

    return (
      <div className="card-body">
        <ul className="list-group">
          {Object.entries(transaction || {}).map(([key, value], i) => (
            <li
              className="list-group-item"
              key={i}
              style={{ flexWrap: "wrap" }}
            >
              {renderValueView({ key, value })}
            </li>
          ))}
        </ul>
      </div>
    );
  }
}

export default SingleTransactionView;
